// Onboarding workspace: the nine sections of a project's setup, their order and
// dependencies, the states a section can be in, and the tag that says where a
// proposed value came from (docs/v3/onboarding-proposal/).
import { z } from "zod";
import { CONTRACT_KINDS, REPORT_KINDS, type ActivityType, type DeliverableKind, type ImportJobStatus, type ImportPile, type ReportKind } from "../enums";
import { emailOrEmpty, isoDate } from "../schemas/common";
import { contactMapping, type ContactField } from "./stakeholders";

export const ONBOARDING_SECTIONS = ["contract", "results", "budget", "team", "work", "reporting", "stakeholders", "documents", "history"] as const;
export type OnboardingSection = (typeof ONBOARDING_SECTIONS)[number];

/** The sections' names, as the workspace's menu shows them (board A4). */
export const SECTION_LABELS: Record<OnboardingSection, string> = {
  contract: "Contract",
  results: "Results",
  budget: "Budget",
  team: "Team and experts",
  work: "Lines of work and activities",
  reporting: "Reporting and deliverables",
  stakeholders: "Stakeholders",
  documents: "Documents",
  history: "History",
};

/** Dependency order from the map board. */
export const SECTION_ORDER: Record<OnboardingSection, number> = {
  contract: 1,
  results: 2,
  budget: 3,
  team: 4,
  work: 5,
  reporting: 6,
  history: 7,
  stakeholders: 8,
  documents: 9,
};

export const SECTION_DEPENDS_ON: Record<OnboardingSection, OnboardingSection[]> = {
  contract: [],
  results: ["contract"],
  budget: ["contract"],
  team: ["budget"],
  work: ["results", "team"],
  reporting: ["contract", "results"],
  stakeholders: [],
  documents: [],
  history: [],
};

export const SECTION_STATES = ["empty", "to_review", "confirmed", "later", "assigned"] as const;
export type SectionState = (typeof SECTION_STATES)[number];

export const ONBOARDING_TRACKS = ["new", "ongoing"] as const;
export type OnboardingTrack = (typeof ONBOARDING_TRACKS)[number];

/** A draft project in GET /api/projects `drafts` (only its creator sees it there): the onboarding's "Continue setting up". */
export type DraftProjectRow = { id: string; name: string; track: OnboardingTrack; createdAt: string };

export const START_MODES = ["documents", "scratch", "copy"] as const;
export type StartMode = (typeof START_MODES)[number];

export const PERIOD_DETAIL = ["totals", "totals_archive", "full"] as const;
export type PeriodDetail = (typeof PERIOD_DETAIL)[number];

/** Where a proposed value came from: a file, and a page, quote or spreadsheet row. */
export const SOURCE_TAG = z.object({
  fileId: z.string().uuid(),
  fileLabel: z.string(),
  page: z.number().int().nullable(),
  quote: z.string().nullable(),
  row: z.number().int().nullable(),
  /** The article of a contract the value was read from ("26": "Special conditions, art. 26"; Task 2.6). Optional: tags made before it have none. */
  article: z.string().max(20).nullable().optional(),
});
export type SourceTag = z.infer<typeof SOURCE_TAG>;

export const FILE_PILE_TO_SECTIONS: Record<ImportPile, OnboardingSection[]> = {
  contract: ["contract"],
  logframe: ["results"],
  tors: ["team", "reporting"],
  reports: ["history"],
  timesheets: ["history"],
  attendance: ["history"],
  cvs: ["team"],
  contacts: ["stakeholders"],
  evidence: ["history"],
};

/** Short name for a file in source tags. */
export function fileShortLabel(fileName: string): string {
  const base = fileName.replace(/\.[^.\\/]+$/, "");
  if (/terms of reference/i.test(base)) return "ToR";
  return base;
}

// ---------- the onboarding draft (Task 0.3): items and sections ----------

/** An item's state in the draft: read as is, to check, an open item, confirmed, or removed (merged or deleted). */
export const ITEM_STATES = ["read", "check", "open_item", "confirmed", "removed"] as const;
export type ItemState = (typeof ITEM_STATES)[number];

export const SECTION_ACTIONS = ["confirm", "later", "reopen"] as const;
export type SectionAction = (typeof SECTION_ACTIONS)[number];

/**
 * Two documents disagree on one field (a conflict card, Task 2.1): the options
 * found and which one the user picked (null: not yet, a blocker).
 */
export type ItemConflict = {
  field: string;
  options: Array<{ value: string | number | null; quote: string | null; source: SourceTag }>;
  picked: number | null;
};

/** One proposed value in the draft (an import_item, as the workspace shows it). */
export type DraftItem = {
  id: string;
  pile: ImportPile;
  /** The section the item lives in. */
  section: OnboardingSection;
  /** Every section that shows it: a ToR shows in team and reporting until it is moved. */
  sections: OnboardingSection[];
  state: ItemState;
  /** What was read, with the user's edits on top. */
  fields: Record<string, string | number | null>;
  source: SourceTag | null;
  edited: boolean;
  mergedIntoId: string | null;
  /** The result an activity serves (a suggestion until confirmed); follows a merge. */
  resultId: string | null;
  conflict: ItemConflict | null;
  needsCheck: string | null;
};

export type SectionView = {
  section: OnboardingSection;
  state: SectionState;
  assigneeId: string | null;
  laterAt: string | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
  acceptReason: string | null;
};

/**
 * A section given to a colleague (Task 1.4, board A4g): who has it, the note,
 * who gave it and when, and when the last reminder went.
 */
export type SectionAssignment = {
  userId: string;
  name: string;
  note: string | null;
  assignedAt: string | null;
  assignedById: string | null;
  assignedByName: string | null;
  remindedAt: string | null;
};

/**
 * GET /p/:projectId/onboarding/sections/:section. `blockers`: what stops Confirm
 * (blockersFor keys: `conflict:<itemId>:<field>` or an open item's key).
 */
export type SectionDetail = SectionView & { items: DraftItem[]; blockers: string[]; assignment: SectionAssignment | null };

/** POST /p/:projectId/onboarding/sections/:section/assign: an active member of the organisation, with an optional note. */
export const sectionAssignBody = z.object({ userId: z.string().uuid(), note: z.string().trim().max(500).optional() });
export type SectionAssignBody = z.infer<typeof sectionAssignBody>;

/** GET /p/:projectId/onboarding/assignees: the colleagues a section can be given to. */
export type AssigneeList = { people: Array<{ id: string; name: string }> };

/**
 * GET /p/:projectId/onboarding/source/:fileId/pages/:page[?item=<itemId>] (board A4h):
 * the page's text, where the item's quote is on it (offsets in `text`; none when the
 * quote is not on this page), how many pages the file has, and the pages that
 * mention the quote (Previous / Next mention).
 */
export type SourcePage = {
  fileId: string;
  fileName: string;
  page: number;
  pageCount: number;
  text: string;
  highlights: Array<{ start: number; end: number }>;
  mentions: number[];
};

/** PATCH /p/:projectId/onboarding/items/:id: change values, or move it to another section. */
export const draftItemPatch = z.object({
  fields: z.record(z.string().max(60), z.union([z.string().max(2000), z.number(), z.null()])).optional(),
  section: z.enum(ONBOARDING_SECTIONS).optional(),
});
export type DraftItemPatch = z.infer<typeof draftItemPatch>;

/** POST /p/:projectId/onboarding/items/:id/merge: the item in the path is kept. */
export const draftItemMerge = z.object({ dropId: z.string().uuid() });

/** POST /p/:projectId/onboarding/sections/:section/:action */
export const sectionActionBody = z.object({ reason: z.string().trim().max(1000).optional() });

// ---------- open items and counts (Task 0.4) ----------

/**
 * Something still missing in a project's setup, from openItems(): the one
 * source for the section counts, the Ready list, the Home Setup card and the
 * audit readiness view. The key is stable ("cv_missing:<engagement id>",
 * "indicator_no_target:<id>"); its prefix is the kind. `blocking`: the
 * section cannot be confirmed while it is open.
 */
export type OpenItem = {
  key: string;
  section: OnboardingSection;
  title: string;
  detail: string;
  ownerId: string | null;
  status: "open" | "done";
  blocking: boolean;
};

/** One section's counts (sectionSummary). toReview: items read or to check, not yet confirmed. */
export type SectionSummary = {
  section: OnboardingSection;
  state: SectionState;
  toReview: number;
  openItems: number;
  assigneeId: string | null;
  /** The assignee's name (the menu's "waiting for Ana"); null when nobody has it. */
  assigneeName?: string | null;
};

/** GET /p/:projectId/onboarding/summary. Totals count each item once (a ToR shows in two sections). */
export type OnboardingSummary = {
  sections: SectionSummary[];
  totals: { toReview: number; openItems: number; blocking: number };
  /** The draft's name and track (Task 1.4: the workspace's H1 and its sections). */
  project?: { name: string; track: OnboardingTrack; startMode?: StartMode | null };
  /** When the draft last changed (an item or a section), for "saved 2 min ago". */
  savedAt?: string | null;
  /**
   * What the caller may do (Task 1.4, ruling R15): `lead` assigns sections; `canEdit`
   * changes items and sections; `sections` is the only sections a colleague who was
   * given a section may open (null: every section).
   */
  you?: { lead: boolean; canEdit: boolean; sections: OnboardingSection[] | null };
};

/** GET /p/:projectId/onboarding/open-items. Counts are of the open ones (done ones are listed, not counted). */
export type OpenItemList = { items: OpenItem[]; counts: { open: number; done: number; blocking: number } };

/** PATCH /p/:projectId/onboarding/open-items/:key */
export const openItemPatch = z.object({ ownerId: z.string().uuid().nullable().optional(), done: z.boolean().optional() });
export type OpenItemPatch = z.infer<typeof openItemPatch>;

// ---------- the start (Task 1.1, boards A1 and B1) ----------

/** Q1 "Who funds the project?": the European Union or another funder. */
export const DRAFT_FUNDERS = ["eu", "other"] as const;
export type DraftFunder = (typeof DRAFT_FUNDERS)[number];

/** Q2 "What kind of contract is it?": a contract kind, "Other", or null (left to the documents). */
export const DRAFT_CONTRACT_KINDS = [...CONTRACT_KINDS, "other"] as const;
export type DraftContractKind = (typeof DRAFT_CONTRACT_KINDS)[number];

/** B1 route for a running project: Quick start or Full history. */
export const HISTORY_EFFORTS = ["quick", "full"] as const;
export type HistoryEffort = (typeof HISTORY_EFFORTS)[number];

/**
 * POST /api/projects/drafts: the start screen's answers. A copy names the live
 * project it copies (copyFromProjectId, only with startMode "copy"). cutoffDate
 * and effort are for a running project (track "ongoing"); left out, the cut-off
 * is today and the route Quick start.
 */
export const startDraftBody = z
  .object({
    startMode: z.enum(START_MODES),
    funder: z.enum(DRAFT_FUNDERS),
    contractKind: z.enum(DRAFT_CONTRACT_KINDS).nullable(),
    track: z.enum(ONBOARDING_TRACKS),
    cutoffDate: isoDate.optional(),
    effort: z.enum(HISTORY_EFFORTS).optional(),
    copyFromProjectId: z.string().uuid().optional(),
  })
  .superRefine((b, ctx) => {
    if (b.startMode === "copy" && !b.copyFromProjectId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["copyFromProjectId"], message: "Choose the project to copy." });
    if (b.startMode !== "copy" && b.copyFromProjectId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["copyFromProjectId"], message: "Only a copy names a project to copy." });
    if (b.track === "new" && (b.cutoffDate || b.effort)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cutoffDate"], message: "Only a running project has a cut-off date." });
  });
export type StartDraftBody = z.infer<typeof startDraftBody>;

/** POST /api/projects/drafts answer. */
export type StartDraftResult = { projectId: string };

/** GET /api/projects/drafts/copy-sources: the live projects the caller can edit, for "Copy an existing project". */
export type CopySourceList = { projects: Array<{ id: string; name: string; reference: string }> };

// ---------- the documents step (Task 1.2, boards A2 and B3) ----------

/** "Recognised as": what a dropped file is. The guess can be changed per file. */
export const FILE_KINDS = ["contract", "tor", "budget", "logframe", "application", "annex", "template", "not_needed"] as const;
export type FileKind = (typeof FILE_KINDS)[number];
export const FILE_KIND_LABELS: Record<FileKind, string> = {
  contract: "Contract",
  tor: "ToR",
  budget: "Budget",
  logframe: "Logframe",
  application: "Application",
  annex: "Annex",
  template: "Template",
  not_needed: "Not needed",
};

/** "Pile": where a file's findings go in the workspace. Each pile is one section (DOCUMENT_PILE_SECTION). */
export const DOCUMENT_PILES = ["contract", "budget", "team", "background", "reporting", "not_needed"] as const;
export type DocumentPile = (typeof DOCUMENT_PILES)[number];
export const DOCUMENT_PILE_LABELS: Record<DocumentPile, string> = {
  contract: "Contract",
  budget: "Budget",
  team: "Team",
  background: "Background",
  reporting: "Reporting",
  not_needed: "Not needed",
};
/** The section a pile's findings land in; "Not needed" is read into none. */
export const DOCUMENT_PILE_SECTION: Record<DocumentPile, OnboardingSection | null> = {
  contract: "contract",
  budget: "budget",
  team: "team",
  background: "documents",
  reporting: "reporting",
  not_needed: null,
};

/** A dropped file: waiting, being read, read, not readable, or skipped (not needed). */
export const DOCUMENT_FILE_STATES = ["queued", "reading", "read", "unreadable", "skipped"] as const;
export type DocumentFileState = (typeof DOCUMENT_FILE_STATES)[number];

/** One file of GET /p/:projectId/import/files. `id` is the stored file's id; `guessed`: kind and pile are still the guess. */
export type DocumentFile = { id: string; name: string; state: DocumentFileState; kind: FileKind; pile: DocumentPile; guessed: boolean };
export type DocumentFileList = { files: DocumentFile[] };

/** PATCH /p/:projectId/import/files/:fileId: change the guess. "Not needed" in either sets both. */
export const documentFilePatch = z.object({ kind: z.enum(FILE_KINDS).optional(), pile: z.enum(DOCUMENT_PILES).optional() });
export type DocumentFilePatch = z.infer<typeof documentFilePatch>;

/** One document usually expected for the contract kind, with the file that matched it (or null). */
export type ExpectedDocument = {
  key: string;
  label: string;
  /** Needed for Quick start or Full history (a running project); null: good to have. */
  requiredFor: "quick" | "full" | null;
  note?: string;
  file: { id: string; name: string; state: DocumentFileState } | null;
};

/** GET /p/:projectId/import/expected: the side card "For this contract type we usually expect". */
/** `oldImport`: a live project's import from before the onboarding (no draft to build; its own review goes on at /p/:projectId/import/review). */
export type ExpectedDocuments = { track: OnboardingTrack; contractKind: DraftContractKind | null; contractLabel: string; documents: ExpectedDocument[]; oldImport: boolean };

/** POST /p/:projectId/onboarding/build ("Build my project draft"): the files go to be read; nothing is committed. */
export type BuildDraftResult = { projectId: string; status: ImportJobStatus };

// ---------- the reading step (Task 1.3, board A3) ----------

/** One file of GET /p/:projectId/import/progress: its state, how far it is, and what was found in it so far. */
export type ReadingFile = {
  /** The stored file's id (as in GET /import/files). */
  id: string;
  name: string;
  state: DocumentFileState;
  /** 0 waiting, 50 being read (the reader does not say how far into a file it is), 100 done. */
  percent: number;
  /** One line of what was found ("13 results · 4 work packages"), or why the file could not be read; null while waiting or reading. */
  found: string | null;
};

/** What the whole draft has so far, for the figures under the file list. */
export type ReadingTotals = {
  results: number;
  budgetLines: number;
  /** The budget's total (null: no budget lines yet). */
  budgetTotalCents: number | null;
  keyExperts: number;
  cvsRead: number;
  workPackages: number;
};

/**
 * GET /p/:projectId/import/progress (board A3). `read` of `total` files are read
 * (skipped files are not counted in `total`). `etaMinutes`: a rough estimate from
 * the files still waiting or being read, null when none is. `ready`: every file is
 * read or skipped and the draft is built, so the review can open.
 */
export type ReadingProgress = {
  projectName: string;
  /** The import's status (null: no import yet). */
  status: ImportJobStatus | null;
  /** The reading stopped (status "failed"): `error` says why in plain words. */
  failed: boolean;
  error: string | null;
  track: OnboardingTrack;
  read: number;
  total: number;
  etaMinutes: number | null;
  ready: boolean;
  files: ReadingFile[];
  totals: ReadingTotals;
};

/** POST /p/:projectId/import/files/:fileId/replace: a new upload (purpose project_import) in place of an unreadable file. */
export const documentFileReplace = z.object({ fileId: z.string().uuid() });
export type DocumentFileReplace = z.infer<typeof documentFileReplace>;

// ---------- the contract section (Task 2.1, board A4a) ----------

/** The contract's values in the section, in the board's order (the name first: drafts are "New project" until it is set). */
export const CONTRACT_FIELDS = ["name", "funder", "contracting_authority", "reference", "amount", "currency", "start_date", "end_date", "contract_kind"] as const;
export type ContractField = (typeof CONTRACT_FIELDS)[number];

/** The fields two documents can disagree on (a conflict card). */
export const CONTRACT_CONFLICT_FIELDS = ["start_date", "end_date", "amount", "currency", "contract_kind"] as const;
export type ContractConflictField = (typeof CONTRACT_CONFLICT_FIELDS)[number];

/** A value of the contract: what it is now (read or edited), where it was read, and the item that holds it. */
export type ContractValue = { value: string | number | null; source: SourceTag | null; itemId: string | null };

/**
 * Two documents disagree on one field ("Two documents disagree · Pick one"): one
 * option per value, with the passage and the item it was read from. `picked`: the
 * option chosen (null: not yet, a blocker of the section).
 */
export type ContractConflict = {
  field: ContractConflictField;
  options: Array<{ value: string | number; quote: string | null; source: SourceTag; sourceItemId: string }>;
  picked: { value: string | number; sourceItemId: string } | null;
};

export const PARTNER_ROLES = ["lead", "member"] as const;
export type PartnerRole = (typeof PARTNER_ROLES)[number];

/**
 * A partner of the contract. `key`: the partner's item id, or "read-<itemId>-<n>"
 * for one read from the contract that nobody changed yet (its first change makes it an item).
 */
export type ContractPartner = { key: string; name: string; role: PartnerRole; source: SourceTag | null; sourceItemId: string | null };

/**
 * GET /p/:projectId/onboarding/sections/contract/details. `kind`: the contract
 * kind as Q2 answered it (`answer`, null when it was left to the documents),
 * what the documents name, and the value shown: the answer, else what the
 * documents name (`fromDocuments`: the user still confirms it).
 */
export type ContractView = {
  itemId: string | null;
  fields: Record<Exclude<ContractField, "contract_kind">, ContractValue>;
  kind: { answer: DraftContractKind | null; documents: DraftContractKind | null; value: DraftContractKind | null; fromDocuments: boolean; source: SourceTag | null; itemId: string | null };
  conflicts: ContractConflict[];
  partners: ContractPartner[];
};

/** PATCH /p/:projectId/onboarding/sections/contract/details: edit values in place. Dates are ISO days or months. */
export const contractDetailsPatch = z
  .object({
    name: z.string().trim().max(200).nullable(),
    funder: z.string().trim().max(200).nullable(),
    contracting_authority: z.string().trim().max(200).nullable(),
    reference: z.string().trim().max(120).nullable(),
    amount: z.number().int().min(0).max(100_000_000_000_00).nullable(),
    currency: z.string().trim().regex(/^[A-Za-z]{3}$/, "Three letters, e.g. EUR.").nullable(),
    start_date: z.string().regex(/^\d{4}-\d{2}(-\d{2})?$/).nullable(),
    end_date: z.string().regex(/^\d{4}-\d{2}(-\d{2})?$/).nullable(),
    contract_kind: z.enum(DRAFT_CONTRACT_KINDS).nullable(),
  })
  .partial();
export type ContractDetailsPatch = z.infer<typeof contractDetailsPatch>;

/** POST /p/:projectId/onboarding/sections/contract/conflicts/:field: the option picked. */
export const contractConflictPick = z.object({ value: z.union([z.string().max(200), z.number()]), sourceItemId: z.string().uuid() });
export type ContractConflictPick = z.infer<typeof contractConflictPick>;

/** POST /p/:projectId/onboarding/sections/contract/partners ("Add a partner"); PATCH .../partners/:key (Edit). */
export const contractPartnerBody = z.object({ name: z.string().trim().min(1, "Enter the partner's name.").max(200), role: z.enum(PARTNER_ROLES) });
export const contractPartnerPatch = contractPartnerBody.partial();
export type ContractPartnerBody = z.infer<typeof contractPartnerBody>;

// ---------- the results section (Task 2.2, board A4b) ----------

/** An indicator of a result in the draft: what it measures, its target (null: none yet, an open item). */
/** `codeSuggested`: it was read without a code; `code` is the one suggested from its place ("3.4.1"). */
export type ResultIndicator = { id: string; code: string | null; codeSuggested: boolean; title: string; target: string | null; source: SourceTag | null };

/** A result's line in the tree: read as is, to check (a possible duplicate), an open item (an indicator without target), or confirmed. */
export const RESULT_ROW_STATES = ["read", "check", "open_item", "confirmed"] as const;
export type ResultRowState = (typeof RESULT_ROW_STATES)[number];

/**
 * One result of the draft's tree (impact → outcome → output), in tree order.
 * `label`: its level and code ("Output 2.1"; a code without a number is left out:
 * "Impact"). `parentId`: the result it sits under. `duplicateOf`: the code of the
 * result it may be the same as, while that check is open. `codeSuggested`: it was
 * read without a code; `code` is the one suggested from its place (the next free
 * number under its parent), which Start project uses unless it is changed.
 */
export type ResultNode = {
  id: string;
  code: string | null;
  codeSuggested: boolean;
  title: string;
  shortName: string | null;
  levelNo: number;
  label: string;
  parentId: string | null;
  depth: number;
  indicators: ResultIndicator[];
  withoutTarget: number;
  source: SourceTag | null;
  state: ResultRowState;
  duplicateOf: string | null;
};

/** "Output 2.3 may be the same as Output 2.1": `a` is kept by Merge, `b` merged into it. */
export type ResultDuplicate = { a: string; b: string; aLabel: string; bLabel: string; reason: string; sources: Array<{ itemId: string; source: SourceTag }> };

/**
 * GET /p/:projectId/onboarding/sections/results/details. `fromTor`: a service
 * contract whose results were read from its ToR (no logframe was uploaded).
 * `levelNames`: the names of the levels, top first. `withoutTarget`: indicators
 * without a target (open items, not blockers).
 */
export type ResultsView = { fromTor: boolean; levelNames: string[]; results: ResultNode[]; duplicates: ResultDuplicate[]; indicators: number; withoutTarget: number };

/** POST /p/:projectId/onboarding/sections/results/results: "Add a result". */
export const resultAddBody = z.object({
  levelNo: z.number().int().min(1).max(5),
  code: z.string().trim().min(1, "Enter a code, for example 2.4.").max(16),
  title: z.string().trim().min(1, "Enter what the result is.").max(1000),
  parentId: z.string().uuid().nullable().optional(),
});
export type ResultAddBody = z.infer<typeof resultAddBody>;

/** POST /p/:projectId/onboarding/sections/results/duplicates/merge | keep: the pair of a possible duplicate (`b` is merged into `a`). */
export const resultPairBody = z.object({ a: z.string().uuid(), b: z.string().uuid() });
export type ResultPairBody = z.infer<typeof resultPairBody>;

/** POST /p/:projectId/onboarding/sections/results/logframe: "Upload a logframe" (an upload of purpose logframe). */
export const resultsLogframeBody = z.object({ fileId: z.string().uuid() });
export type ResultsLogframeBody = z.infer<typeof resultsLogframeBody>;

/** The answer to "Upload a logframe": what was added to the draft, and the codes the draft already had. */
export type ResultsLogframeResult = { view: ResultsView; fileName: string; added: { results: number; indicators: number; activities: number }; skipped: string[] };

// ---------- the budget section (Task 2.3, board A4c) ----------

/** What a column of the budget sheet holds (the column check, before any line is made). */
export const BUDGET_COLUMN_ROLES = ["code", "name", "unit", "units", "rate", "total", "not_needed"] as const;
export type BudgetColumnRole = (typeof BUDGET_COLUMN_ROLES)[number];

/** One column of the sheet: its letter ("A"), its header ("Code") and what it is read as. */
export type BudgetColumn = { column: string; header: string; role: BudgetColumnRole };

/**
 * The kind of a draft budget line, from its heading: a key expert's fees (fees),
 * the non-key experts' days (expert_pool), incidentals and other costs (other),
 * expenditure verification, contingency. Start project writes expert_pool as fees.
 */
export const BUDGET_DRAFT_KINDS = ["fees", "expert_pool", "other", "verification", "contingency"] as const;
export type BudgetDraftKind = (typeof BUDGET_DRAFT_KINDS)[number];

const KEY_EXPERT = /\bKE\s?\d|\bkey experts?\b/i;
const NON_KEY = /non.?key|\bnke\b|expert pool|\bpool\b/i;

/**
 * The heading sets the kind (board A4c): under Fees a key expert's line is fees and
 * a non-key line the expert pool; under Incidental expenditure other; under
 * Expenditure verification verification. Without such a heading, the name says it.
 */
export function budgetDraftKind(heading: string | null, name: string): BudgetDraftKind {
  const h = heading ?? "";
  if (/contingenc|\breserve\b/i.test(h) || /contingenc|\breserve\b|unforeseen/i.test(name)) return "contingency";
  if (/verification/i.test(h) || /expenditure verification|verification of expenditure/i.test(name)) return "verification";
  if (/incidental/i.test(h)) return "other";
  // A heading that names the pools ("Fees: non-key expert pools", the template's) makes its lines pool lines.
  if (/\bfees?\b|human resources|\bexperts?\b/i.test(h)) return NON_KEY.test(name) || NON_KEY.test(h) ? "expert_pool" : "fees";
  if (NON_KEY.test(name)) return "expert_pool";
  if (KEY_EXPERT.test(name) || /\bexperts?\b|\bstaff\b|\bpersonnel\b/i.test(name)) return "fees";
  return "other";
}

/** The kind a budget line is stored with once the project starts (BUDGET_LINE_KINDS). */
export const liveBudgetKind = (k: BudgetDraftKind): "fees" | "other" | "verification" | "contingency" => (k === "expert_pool" ? "fees" : k);

/**
 * The tag of a line's kind; "other" under Incidental expenditure is Incidentals (board A4c).
 * A grant's people lines are staff costs, not fees (round 4, OB-F8: `feesLabel` from contractWording).
 */
export function budgetKindLabel(kind: BudgetDraftKind, heading: string | null, feesLabel = "Fees"): string {
  if (kind === "other") return /incidental/i.test(heading ?? "") ? "Incidentals" : "Other";
  return { fees: feesLabel, expert_pool: feesLabel === "Fees" ? "Expert pool" : "Staff category", verification: "Expenditure verification", contingency: "Contingency" }[kind];
}

/** A line of a budget sheet as read: its sheet row, the values of the columns, its heading and kind. */
export type BudgetSheetLine = {
  row: number;
  code: string | null;
  name: string;
  unit: string | null;
  units: number | null;
  rateCents: number | null;
  totalCents: number;
  heading: string | null;
  kind: BudgetDraftKind;
};

/**
 * A row of the sheet that is not a line: a sub-total or total (Convenor adds up the
 * lines itself), or empty. `explained`: its amount is what the lines above add up to.
 */
export type BudgetSkippedRow = { row: number; reason: "subtotal" | "total" | "empty"; name: string; amountCents: number | null; heading: string | null; explained: boolean };

/** A sheet read with a column check (readBudgetSheet). `mismatches`: rows where units × rate is not the total. */
export type BudgetSheetRead = {
  sheet: string;
  sheets: string[];
  headerRow: number;
  columns: BudgetColumn[];
  headings: string[];
  lines: BudgetSheetLine[];
  skippedRows: BudgetSkippedRow[];
  mismatches: Array<{ row: number; productCents: number; totalCents: number }>;
  /** The first lines' cells as the sheet shows them, one per column. */
  preview: Array<{ row: number; cells: string[] }>;
};

/** POST /onboarding/sections/budget/files/:fileId/columns: the role of each column (none: as guessed from the headers), and the sheet. */
export const budgetColumnsBody = z.object({
  sheet: z.string().trim().min(1).max(100).optional(),
  columns: z
    .array(z.object({ column: z.string().regex(/^[A-Z]{1,3}$/), role: z.enum(BUDGET_COLUMN_ROLES) }))
    .max(200)
    .optional(),
});
export type BudgetColumnsBody = z.infer<typeof budgetColumnsBody>;

/** POST /onboarding/sections/budget/lines: "Add a budget line" by hand (a draft started from scratch has none). The heading sets the kind. */
export const budgetLineAddBody = z.object({
  name: z.string().trim().min(1, "A budget line needs a name.").max(200),
  heading: z.string().trim().max(200).nullable().optional(),
  amountCents: z.number().int().min(0).max(1_000_000_000_000),
});
export type BudgetLineAddBody = z.infer<typeof budgetLineAddBody>;

/** POST /onboarding/sections/budget/difference/line: "Add it as a line" (the skipped row's item). */
export const budgetAddRowBody = z.object({ itemId: z.string().uuid() });
/** POST /onboarding/sections/budget/difference/accept: "Accept with a reason". */
export const budgetAcceptBody = z.object({ reason: z.string().trim().min(1, "Say why the budget differs from the contract.").max(1000) });
export type BudgetAcceptBody = z.infer<typeof budgetAcceptBody>;

/** The column check of one budget file. `confirmed`: its lines were made with these columns; `edited`: a line of it was changed. */
export type BudgetFileCheck = {
  fileId: string;
  fileName: string;
  sheet: string | null;
  sheets: string[];
  columns: BudgetColumn[];
  preview: Array<{ row: number; cells: string[] }>;
  skipped: number;
  mismatches: number[];
  confirmed: boolean;
  edited: boolean;
  /** Why the file could not be read with these columns (null: it could). */
  problem: string | null;
};

export type BudgetLineView = {
  id: string;
  row: number | null;
  code: string | null;
  name: string;
  heading: string | null;
  kind: BudgetDraftKind;
  unit: string | null;
  units: number | null;
  rateCents: number | null;
  totalCents: number;
  source: SourceTag | null;
  /** Added by hand ("Add it as a line"), not read as a line. */
  added: boolean;
};

/** A row read as a sub-total that the lines above do not explain: it may be a line (the difference banner). */
export type BudgetSuspect = { itemId: string; row: number | null; name: string; amountCents: number; source: SourceTag | null };

/**
 * The lines against the contract amount (the Contract section's value), also with no line yet (`lines` 0).
 * `reason`: the difference was accepted with this reason (it counts only for this difference).
 * `blocking`: Confirm stays off.
 */
export type BudgetDifferenceView = { totalCents: number; lines: number; contractCents: number; differenceCents: number; suspects: BudgetSuspect[]; reason: string | null; blocking: boolean };

/** GET /p/:projectId/onboarding/sections/budget/details. */
export type BudgetSectionView = {
  files: BudgetFileCheck[];
  headings: Array<{ heading: string; totalCents: number; lines: BudgetLineView[] }>;
  lines: number;
  totalCents: number;
  /** Null while there is no contract amount, or nothing to compare it with. */
  difference: BudgetDifferenceView | null;
};

// ---------- the team section (Task 2.4, board A4d) ----------

/**
 * A name that looks cut off by a line break (board A4d: "Dr. Katar"): the line it
 * was read from ends with the name, and the name's last word is short (5 letters
 * or fewer). Only a check: the person decides.
 */
export function looksCutOff(name: string | null | undefined, sourceLine: string | null | undefined): boolean {
  const n = (name ?? "").trim().replace(/\s+/g, " ");
  const line = (sourceLine ?? "").trim().replace(/\s+/g, " ");
  if (!n || !line) return false;
  if (!line.toLowerCase().endsWith(n.toLowerCase())) return false;
  const last = n.split(" ").pop() ?? "";
  return last.replace(/[^\p{L}]/gu, "").length <= 5;
}

/** A key expert's CV slot: one CV per person, linked, found in the documents (to accept), or none. */
export type TeamCvState = "linked" | "suggested" | "missing";
export type TeamCv = { state: TeamCvState; fileId: string | null; fileName: string | null; cvItemId: string | null };

/** One key expert of the draft: code, role, name and email as read or typed; days and rate from its budget line (read-only here). */
export type TeamKeyExpert = {
  id: string;
  code: string;
  keNumber: number | null;
  role: string;
  name: string;
  email: string | null;
  /** From the budget line of this KE (null: no line for it, a check). */
  days: number | null;
  rateCents: number | null;
  budgetItemId: string | null;
  cv: TeamCv;
  source: SourceTag | null;
  /** The name looks cut off (looksCutOff). */
  cutOff: boolean;
  added: boolean;
};

/** A non-key expert pool: days and a rate per category, from the budget; no names. */
export type TeamPool = { id: string; name: string; level: "senior" | "junior" | null; days: number | null; rateCents: number | null; source: SourceTag | null };

/** A check card of the section: a name that looks cut off, a key expert without a budget line. Never a blocker. */
export type TeamCheck = { kind: "cut_off" | "no_budget_line"; itemId: string; code: string; title: string; body: string; page: number | null };

/** GET /p/:projectId/onboarding/sections/team/details */
export type TeamSectionView = { keyExperts: TeamKeyExpert[]; pools: TeamPool[]; checks: TeamCheck[]; emailsMissing: number; cvsMissing: number };

/** POST /p/:projectId/onboarding/sections/team/people: "Add a person" (a key expert; the next KE code). */
export const teamPersonBody = z.object({
  role: z.string().trim().min(1, "Enter the role.").max(120),
  name: z.string().trim().min(1, "Enter the name.").max(120),
  email: emailOrEmpty.nullable().optional(),
});
export type TeamPersonBody = z.infer<typeof teamPersonBody>;

/** PATCH /p/:projectId/onboarding/sections/team/people/:itemId: name, email (optional until invited) or role. */
export const teamPersonPatch = z.object({
  role: z.string().trim().min(1, "Enter the role.").max(120).optional(),
  name: z.string().trim().min(1, "Enter the name.").max(120).optional(),
  email: emailOrEmpty.nullable().optional(),
});
export type TeamPersonPatch = z.infer<typeof teamPersonPatch>;

/** POST /p/:projectId/onboarding/sections/team/people/:itemId/cv: a CV uploaded for this person, or the CV found in the documents. */
export const teamCvBody = z.union([z.object({ fileId: z.string().uuid() }).strict(), z.object({ cvItemId: z.string().uuid() }).strict()]);
export type TeamCvBody = z.infer<typeof teamCvBody>;

// ---------- the work packages and activities section (Task 2.5, board A4e) ----------

/** What an activity is, as the board names it (any kind can sit in any work package). */
export const WORK_KINDS = ["study", "training", "workshop", "exercise", "study_visit", "conference", "meeting", "expert_mission", "communication"] as const;
export type WorkKind = (typeof WORK_KINDS)[number];
export const WORK_KIND_LABELS: Record<WorkKind, string> = {
  study: "Study",
  training: "Training",
  workshop: "Workshop",
  exercise: "Exercise",
  study_visit: "Study visit",
  conference: "Conference",
  meeting: "Meeting",
  expert_mission: "Expert mission",
  communication: "Communication",
};
/** The activity type a kind becomes at Start project: studies and communication are deliverables, expert missions (N)KE activities, the rest events. */
export const WORK_KIND_TYPE: Record<WorkKind, ActivityType> = {
  study: "deliverable",
  training: "event",
  workshop: "event",
  exercise: "event",
  study_visit: "event",
  conference: "event",
  meeting: "event",
  expert_mission: "nke",
  communication: "deliverable",
};
/** A deliverable's own kind at Start project (deliverable.kind). */
export const WORK_KIND_DELIVERABLE: Partial<Record<WorkKind, DeliverableKind>> = { study: "study", communication: "other" };

export const isWorkKind = (v: unknown): v is WorkKind => typeof v === "string" && (WORK_KINDS as readonly string[]).includes(v);

/**
 * The months of an activity, counted from the contract start (M1 = its first
 * month): a span "M1–M4", one month "M9", or a list "M12, M24, M36" (from the
 * first to the last; the list is kept for the timeline marks). Null when unreadable.
 */
export type WorkMonths = { from: number; to: number; list: number[] | null };
export function parseMonths(text: string | null | undefined): WorkMonths | null {
  const s = (text ?? "").trim();
  if (!s) return null;
  const span = /^M?\s*(\d{1,3})\s*[–—-]\s*M?\s*(\d{1,3})$/i.exec(s);
  if (span) {
    const a = Number(span[1]);
    const b = Number(span[2]);
    return a >= 1 && b >= a ? { from: a, to: b, list: null } : null;
  }
  const parts = s.split(/\s*[,;]\s*|\s+and\s+/i).filter(Boolean);
  const nums = parts.map((p) => /^M?\s*(\d{1,3})$/i.exec(p)?.[1]).map((x) => (x === undefined ? NaN : Number(x)));
  if (nums.length === 0 || nums.some((n) => !Number.isInteger(n) || n < 1)) return null;
  const list = [...new Set(nums)].sort((a, b) => a - b);
  return list.length === 1 ? { from: list[0], to: list[0], list: null } : { from: list[0], to: list[list.length - 1], list };
}
/** "M1–M4", "M9", "M12, M24, M36". */
export function monthsLabel(m: WorkMonths | null): string | null {
  if (!m) return null;
  if (m.list) return m.list.map((n) => `M${n}`).join(", ");
  return m.from === m.to ? `M${m.from}` : `M${m.from}–M${m.to}`;
}

/** Why a result is suggested for an activity. */
export const WORK_REASONS = ["same code", "same words", "nearest outcome"] as const;
export type WorkReason = (typeof WORK_REASONS)[number];

/**
 * A suggested result: `detail` is the reason as the row says it ("same code",
 * "“CERT” in both texts", "no output matches; nearest outcome"). Only a
 * `confident` one is taken by "Accept N clear suggestions".
 */
export type WorkSuggestion = { resultId: string; label: string; reason: WorkReason; detail: string; confident: boolean };

/** One activity of the draft (an import_item, fields.type event | deliverable | nke). */
export type WorkActivity = {
  id: string;
  code: string;
  title: string;
  kind: WorkKind | null;
  /** "Study"; without a kind, its type ("Event"). */
  kindLabel: string;
  /** "M1–M4" as read, normalised; null when not said. */
  months: string | null;
  from: number | null;
  to: number | null;
  workPackageId: string | null;
  suggestion: WorkSuggestion | null;
  /** The result it serves once accepted or changed (follows a merge); null until then. */
  result: { id: string; label: string } | null;
  source: SourceTag | null;
  state: ItemState;
};

/** A work package (stored as a line of work at Start project). `id` null: the activities in no work package. */
export type WorkPackage = { id: string | null; code: string | null; name: string; activities: WorkActivity[]; from: number | null; to: number | null; source: SourceTag | null };

/** The timeline's frame: from the contract section's start date, its length in months, and its quarters ("Q4 26"). */
export type WorkTimeline = { start: string | null; months: number | null; quarters: Array<{ label: string; fromMonth: number; toMonth: number }> };

/**
 * GET /p/:projectId/onboarding/sections/work/details. `clear`: confident
 * suggestions not yet accepted ("Accept 9 clear suggestions"); `needLook`: the
 * codes of activities whose suggestion is not confident ("2 suggestions need a
 * look (A2.3, A2.5)"). `results`: what "Change" may pick, in tree order.
 */
export type WorkSectionView = {
  timeline: WorkTimeline;
  packages: WorkPackage[];
  results: Array<{ id: string; label: string; title: string }>;
  clear: number;
  needLook: string[];
};

/** POST /p/:projectId/onboarding/sections/work/activities/:itemId/result: Accept (no resultId: the suggestion) or Change (the result picked). */
export const workResultBody = z.object({ resultId: z.string().uuid().optional() });
export type WorkResultBody = z.infer<typeof workResultBody>;

/** POST /p/:projectId/onboarding/sections/work/packages: "Add a work package" (the next WP code when none is given). */
export const workPackageBody = z.object({
  name: z.string().trim().min(1, "Enter the name of the work package.").max(200),
  code: z.string().trim().max(16).optional(),
});
export type WorkPackageBody = z.infer<typeof workPackageBody>;

// ---------- the reporting and deliverables section (Task 2.6, board A4f) ----------

/**
 * One report of the plan: its kind and number ("Progress report 2"), when it is due, the rule
 * that set the date ("Every 6 months") and where the rule was read (special conditions art. 26;
 * the budget's expenditure verification line). `key` names it for Remove and a changed date:
 * the plan's "progress-2", or "added-1" for one added by hand.
 */
export type ReportPlanRow = {
  key: string;
  kind: ReportKind;
  number: number;
  title: string;
  /** The period it reports on. */
  periodFrom: string;
  periodTo: string;
  dueDate: string;
  rule: string;
  /** The date was changed by hand. */
  edited: boolean;
  source: SourceTag | null;
  /** The draft item the source tag opens (?source=). */
  sourceItemId: string | null;
};

/** A deliverable the ToR lists, with the month it gives ("Month 4") and the due date worked out from the contract start. */
export type ReportingDeliverable = {
  id: string;
  title: string;
  /** What the ToR says ("Month 4"); null when it says nothing. */
  inTor: string | null;
  /** The month the date is worked out from; null when the ToR gives no clear month. */
  month: number | null;
  dueDate: string | null;
  edited: boolean;
  source: SourceTag | null;
  state: ItemState;
};

/** GET /p/:projectId/onboarding/sections/reporting/details */
export type ReportingSectionView = {
  /** The contract type the reports follow (the start screen's answer, else the documents'). */
  contractKind: DraftContractKind | null;
  /** "Service contract, fee-based: inception, progress, final and expenditure verification." */
  kindLine: string;
  /** The contract's dates the plan is worked out from; null while the Contract section has none. */
  start: string | null;
  end: string | null;
  /** How often the progress or interim reports come, in months, and where that was read. */
  everyMonths: number;
  reports: ReportPlanRow[];
  deliverables: ReportingDeliverable[];
  withoutDate: number;
};

/** POST /p/:projectId/onboarding/sections/reporting/reports: "Add a report". */
export const reportingAddBody = z.object({ kind: z.enum(REPORT_KINDS), dueDate: isoDate });
export type ReportingAddBody = z.infer<typeof reportingAddBody>;

/** PATCH …/reporting/reports/:key and …/reporting/deliverables/:itemId: the due date (null: back to the one worked out). */
export const reportingDuePatch = z.object({ dueDate: isoDate.nullable() });
export type ReportingDuePatch = z.infer<typeof reportingDuePatch>;

// ---------- the stakeholders section (Task 2.7, board A4g) ----------

/** The role an organisation has in the project, as the documents name it. */
export const STAKEHOLDER_ROLES = ["contracting_authority", "beneficiary", "consortium_member", "partner"] as const;
export type StakeholderRole = (typeof STAKEHOLDER_ROLES)[number];
export const STAKEHOLDER_ROLE_LABELS: Record<StakeholderRole, string> = {
  contracting_authority: "Contracting authority",
  beneficiary: "Beneficiary",
  consortium_member: "Consortium member",
  partner: "Partner",
};

/**
 * Where an organisation or a person of the section came from: read from a document,
 * taken from the Contract section (the contracting authority, the partners), a row of
 * a contact list ("Import a contact list", or a spreadsheet of the documents step),
 * or added by hand.
 */
export const STAKEHOLDER_ORIGINS = ["document", "contract", "contact_list", "by_hand"] as const;
export type StakeholderOrigin = (typeof STAKEHOLDER_ORIGINS)[number];

/** A contact person of the draft. No consent is recorded for them: an import records none (the stakeholders module's rule). */
export type StakeholderContact = {
  id: string;
  name: string;
  email: string | null;
  position: string | null;
  origin: StakeholderOrigin;
  source: SourceTag | null;
};

/**
 * An organisation of the draft. `key` names it in its open item (contact_missing:<key>):
 * its draft item's id, or "n-…" (from its name) for one taken from the Contract section.
 * `sourceItemId`: the draft item its source tag opens (?source=). No logo, ever.
 */
export type StakeholderOrganisation = {
  key: string;
  itemId: string | null;
  name: string;
  role: StakeholderRole | null;
  origin: StakeholderOrigin;
  source: SourceTag | null;
  sourceItemId: string | null;
  contacts: StakeholderContact[];
};

/** GET /p/:projectId/onboarding/sections/stakeholders/details */
export type StakeholdersView = {
  organisations: StakeholderOrganisation[];
  /** People whose row names no organisation. */
  unattached: StakeholderContact[];
  /** Organisations without a contact person (each an open item, contact_missing, that does not block). */
  contactsMissing: number;
};

/** POST /p/:projectId/onboarding/sections/stakeholders/organisations: "Add an organisation". */
export const stakeholderOrgBody = z.object({ name: z.string().trim().min(1, "Enter the name.").max(200), role: z.enum(STAKEHOLDER_ROLES) });
export type StakeholderOrgBody = z.infer<typeof stakeholderOrgBody>;

/** POST …/stakeholders/contacts/preview: an uploaded Excel or CSV contact list (POST /api/files first); with `mapping`, the columns as changed. */
export const stakeholderContactsPreviewBody = z.object({ fileId: z.string().uuid(), mapping: contactMapping.optional() });
/** POST …/stakeholders/contacts/import: the list with the columns as checked. */
export const stakeholderContactsImportBody = z.object({ fileId: z.string().uuid(), mapping: contactMapping });
export type StakeholderContactsImportBody = z.infer<typeof stakeholderContactsImportBody>;

/**
 * What a contact list would add to the draft: the columns as guessed, how many people are
 * new, how many are already in the draft (matched: same email, or same name at the same
 * organisation) and how many rows have no name.
 */
export type StakeholderContactsPreview = {
  fileId: string;
  fileName: string;
  rows: number;
  columns: { header: string; sample: string[]; field: ContactField | "ignore" }[];
  counts: { new: number; matched: number; skipped: number };
  matches: { name: string; matchedTo: string; by: "Same email" | "Same name and organisation" }[];
};

/** The answer of …/stakeholders/contacts/import. */
export type StakeholderContactsResult = { view: StakeholdersView; counts: StakeholderContactsPreview["counts"] };

// ---------- the documents section (Task 2.8, boards A2, A5) ----------

/**
 * GET /p/:projectId/onboarding/sections/documents/details: the draft's files with their pile
 * (as on the documents step; "Not needed" files too). `piles`: how many piles hold a file
 * ("7 files in 5 piles"), "Not needed" left out. A pile is changed with PATCH /import/files/:fileId.
 */
export type DocumentsSectionView = { files: DocumentFile[]; piles: number };

// ---------- starting from scratch (Task 2.8, board A4 From scratch) ----------

/** POST /p/:projectId/onboarding/template ("Use the template"): how many draft items it added (0: the draft had them all). */
export type OnboardingTemplateResult = { added: number };

// ---------- Ready to start (Task 3.1, board A5) ----------

/** One row of "The project in short": the section, its main figure and a muted second line, from the section's own view. */
export type ReadySummaryRow = { section: OnboardingSection; headline: string; detail: string };

/** A person the Team section names, invited at Start when there is an email address (null: "Email missing", not invited). */
/** `itemId`: the draft's key expert; Start invites only the ones ticked on the Ready page (round 4, OB-F15). */
export type ReadyInvitee = { itemId: string; name: string; role: string; email: string | null };

/** What keeps Start off: a blocking open item, its section and the reason in plain words (the open item's title). */
export type ReadyBlocker = { key: string; section: OnboardingSection; reason: string };

/**
 * GET /p/:projectId/onboarding/ready (the lead's page; a colleague given some sections gets 404).
 * `openItems`: the open items still to do that are not blockers (blockers are in `blockers`).
 * `people`: who may own an open item, with the names of the owners. `messagePreview`: the
 * invitation's text (paragraphs separated by a blank line) the first invitee with an email
 * gets at Start; `message` its head (null: nobody to invite). `toReview`: the sections still
 * to review: they start with their items as they are but stay "to review" (round 4, X-68:
 * Start confirms nothing), and count under Still open (OB-F7). `canStart`: no blockers, and
 * the caller is the lead. `project.startMode`: a project from scratch skipped Documents and Reading.
 */
export type ReadyView = {
  project: { name: string; track: OnboardingTrack; startMode?: StartMode | null };
  summary: ReadySummaryRow[];
  openItems: OpenItem[];
  people: Array<{ id: string; name: string }>;
  invitees: ReadyInvitee[];
  blockers: ReadyBlocker[];
  messagePreview: string;
  message: { to: string; subject: string; button: string } | null;
  toReview: OnboardingSection[];
  canStart: boolean;
  you: { lead: boolean; canEdit: boolean };
};

// ---------- Start project (Task 3.2) ----------

/**
 * POST /p/:projectId/onboarding/start (the lead): the draft becomes the live project in one go.
 * Refused with 409 not_ready (details { blockers: ReadyBlocker[] }) while a blocker is left, and
 * with 409 already_started (details { projectId }) once it was started.
 */
/** POST /p/:projectId/onboarding/start: the key experts to invite (ReadyInvitee.itemId). None ticked: nobody gets an email (OB-F15). */
export const startProjectBody = z.object({ invite: z.array(z.string().uuid()).max(500).default([]) });
export type StartProjectBody = z.input<typeof startProjectBody>;
export type StartProjectResult = { projectId: string };

// ---------- Settings › Project setup and late documents (Task 3.4) ----------

/** One thing the setup put in the live project (an item of the draft that made a record). */
export type SetupItem = { id: string; label: string; source: SourceTag | null };

/** A screen of the project, as a link: where a value is changed by hand. */
export type SetupLink = { label: string; path: string };

/**
 * One value a document added after the start reads differently from the live project. `apply`:
 * accepting it and confirming the section writes it; false: it is only shown, with `where` it is
 * changed by hand ("Differs from the project: change it in <where>").
 */
export type LateChange = { field: string; label: string; live: string | null; read: string | null; apply: boolean; where: SetupLink | null };

/**
 * One item read from a document added after the start, waiting for its section to be confirmed
 * again. `match`: "new" (nothing like it in the project: confirming adds it), "same" (the project
 * has it already with the same values: confirming adds nothing), "change" (the project has it with
 * other values: `changes`; those with `apply` are written only when accepted and the section is
 * confirmed), "kept" (nothing in the project is made from it: confirming keeps its file with the
 * project's documents; `note` and `link` say what else to do, e.g. the budget import). `decision`:
 * null until the user accepts the change or keeps the current value (confirming keeps the current
 * value then).
 */
export type LateItem = {
  id: string;
  label: string;
  source: SourceTag | null;
  match: "new" | "same" | "change" | "kept";
  changes: LateChange[];
  decision: "accept" | "keep" | null;
  note: string | null;
  link: SetupLink | null;
};

/** A section of Settings › Project setup: what the setup made, read-only, and what a later document brought. */
export type SetupSectionView = {
  section: OnboardingSection;
  state: SectionState;
  confirmedAt: string | null;
  setUp: SetupItem[];
  late: LateItem[];
  /** What keeps "Confirm the section" off (the workspace's blockers of the section). */
  blockers: string[];
};

/** A batch of documents added after the start (one import job; Task 7.1 lists them as batches). */
export type SetupBatch = {
  id: string;
  createdAt: string;
  createdByName: string | null;
  files: number;
  status: ImportJobStatus;
  /** Still being read: the page asks again until it is done. */
  reading: boolean;
};

/** GET /p/:projectId/onboarding/setup (a live project; viewers read it). */
export type ProjectSetupView = {
  project: { name: string; startedAt: string | null };
  /** Started through the onboarding: documents can be added after the start. */
  onboarded: boolean;
  sections: SetupSectionView[];
  openItems: OpenItem[];
  people: Array<{ id: string; name: string }>;
  batches: SetupBatch[];
  you: { lead: boolean; canEdit: boolean };
};

/** POST /p/:projectId/onboarding/late/files (the lead): uploads (purpose project_import) read as one new batch. */
export const lateFilesBody = z.object({ fileIds: z.array(z.string().uuid()).min(1).max(100) });
export type LateFilesBody = z.infer<typeof lateFilesBody>;
export type LateFilesResult = { batchId: string };

/** PATCH /p/:projectId/onboarding/late/items/:id (the lead): accept the suggested change, or keep the current value. */
export const lateDecisionBody = z.object({ accept: z.boolean() });
export type LateDecisionBody = z.infer<typeof lateDecisionBody>;
